/**
 * Shared shell behind every per-type SharingSetup: a full-screen step inside the
 * topic's edit flow that mounts the shared FormBuilder, seeded from the edit
 * form's draft custom form or the type's default share form.
 *
 * It does not save. The topic's edit form owns the one save in this flow, so
 * edits here flow straight into its draft (the sharing form into
 * `customTemplates`, the Settings tab into `reshareStorySettings`) and are
 * persisted by that form's Update button. That keeps a single Done in the hierarchy: a
 * nested editor with its own Cancel/Done would present two controls that look
 * like the one that dismisses this view, and save different things.
 *
 * Back returns to the edit form with the draft intact.
 */

import React from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonContent,
} from '@ionic/react';
import type { TemplateField } from '@/modules/template';
import { FormBuilder, FormLogic } from '@/modules/form';
import type { SharingSetupProps } from './types';
import styles from './SharingSetupModal.module.css';

interface SharingSetupModalProps extends SharingSetupProps {
  /** Seed form when the draft has no custom form. */
  defaultShareFormYaml: string;
  /** Type-specific tokens the author can inject into the message. */
  injectableFields: readonly TemplateField[];
}

const SharingSetupModal: React.FC<SharingSetupModalProps> = ({
  isOpen,
  onClose,
  draft,
  defaultShareFormYaml,
  injectableFields,
}) => {
  const { customTemplates } = draft;
  // Seed from the draft custom form only when it still parses as a current
  // garden form; otherwise (never customized, or saved in a format the parser no
  // longer accepts) start from the type default so the author can set a fresh
  // one, overwriting the unusable payload on the edit form's save.
  const usableCustom =
    customTemplates &&
    FormLogic.isSharingConfig(customTemplates, draft.topicType)
      ? customTemplates
      : null;
  const seed = usableCustom ?? defaultShareFormYaml;

  // A draft that holds a form the builder could not seed from — the corrupt /
  // legacy case, the one the share wizard warns about. The builder is showing
  // the default in its place, so it cannot tell on its own that the topic has
  // anything to put back; told, it keeps offering revert, which is how the
  // author replaces the unusable payload with a form that works.
  const savedFormUnusable = !!customTemplates && usableCustom === null;

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose}>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            {/*
              IonBackButton for the platform's own back affordance — a chevron
              with "Back" on iOS, a bare arrow on Android — rather than one icon
              for both. This view is a step in the edit flow, not a route, so
              `defaultHref` only satisfies its render gate — without one it
              hides itself — and `onClick` replaces the wrapper's router call,
              which it applies before spreading props. The prop is missing from
              its React types but honored at runtime, hence the cast; a handler
              on the surrounding slot cannot work, because that router call
              stops propagation before the click leaves the button.
            */}
            <IonBackButton
              defaultHref="#"
              data-testid="form-builder-back"
              {...({ onClick: onClose } as { onClick: () => void })}
            />
          </IonButtons>
          <IonTitle>Sharing Setup</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className={styles.content}>
        <FormBuilder
          formYaml={seed}
          defaultFormYaml={defaultShareFormYaml}
          injectableFields={injectableFields}
          hasImpressionGoal={draft.hasImpressionGoal}
          topicType={draft.topicType}
          onChange={draft.setCustomTemplates}
          reshareStorySettings={draft.reshareStorySettings}
          onReshareStorySettingsChange={draft.setReshareStorySettings}
          defaultStartingSenderAppositive={
            draft.defaultStartingSenderAppositive
          }
          savedFormUnusable={savedFormUnusable}
        />
      </IonContent>
    </IonModal>
  );
};

export default SharingSetupModal;
